<template>
  <div class="landing-hero-container">

    <!-- ROW 1: NO ALGORITHM (First — hero statement) -->
    <div class="hero-row row-left algo-row">
      <div class="algo-text-full column">
        <h2 class="row-title">Algorithm free.</h2>
        <p class="algo-manifesto">
          A chronological timeline — like the early internet.<br />
          No ads. No manipulation. No doom-scrolling.
        </p>

        <div class="feed-section">
          <div class="feed-toggle-area">
            <SingleSelectToggleRow
              v-model="selectedFeed"
              :options="feedOptions"
              variant="segmented"
              :wrap="false"
              press-on-focus
              aria-label="Landing feed scope"
            />
          </div>

          <!-- Real public feed / org placeholder -->
          <LandingFeedPreview
            :feedMode="selectedFeed"
            @register="$emit('register')"
          />
        </div>
      </div>
    </div>

    <!-- ROW 2: MINT ANYTHING (LEFT aligned) -->
    <div class="hero-row row-left mint-row">
      <div class="row-text-side column">
        <h2 class="row-title">
          Mint Anything.<br />
          <span class="highlight">Forever.</span>
        </h2>
        <p class="row-subtitle">
          Preserve your memories, art, and moments on the blockchain in any
          format: PDF, JPG, Video, Text. No subscriptions. No algorithm. Just
          ownership.
        </p>
        <div class="row alignCentreRow cta-row">
          <UnitButton
            class="primary-btn"
            variant="primary"
            @click="$emit('login')"
            v-haptic
          >
            Log in
          </UnitButton>
          <small class="grey">or</small>
          <UnitButton
            class="secondary-btn"
            variant="secondary"
            @click="$emit('register')"
            v-haptic
          >
            Register for free
          </UnitButton>
        </div>
      </div>

      <!-- Three.js 3D polaroid slideshow -->
      <div class="row-visual-side mint-slideshow">
        <div ref="mintCanvasWrap" class="polaroid-canvas-wrap"></div>
      </div>
    </div>

    <!-- ROW 3: FREE CARA VAGGIO (RIGHT aligned) -->
    <div class="hero-row row-right">
      <div class="row-text-side column">
        <h2 class="row-title">
          Free Cara Vaggio.<br />
          <span class="highlight">NFT Airdrop.</span>
        </h2>
        <p class="row-subtitle">
          The first 1,000 users receive an original Cara Vaggio as a free NFT.
          Claim yours just by signing up.
        </p>
        <p class="row-description">
          An NFT collection created using Cara Vaggio creator machines —
          software created by state-of-the-art models (Claude Opus 4.6 and
          ChatGPT 5.2) to recreate the chiaroscuro, drama, and intensity of
          the master's brushwork. Art critique text by the same models.
        </p>
        <span class="gift-badge">★ First 1,000 Users</span>

        <!-- Join / Invite CTA -->
        <UnitButton
          class="secondary-btn cv-join-btn"
          variant="secondary"
          @click="$emit('register')"
          v-haptic
        >
          Join the Cara Vaggio Airdrop
        </UnitButton>
      </div>

      <!-- Stacked deck with flip -->
      <div class="row-visual-side deck-visual">
        <div class="deck-container">
          <div
            v-for="(card, index) in caraVaggioCards"
            :key="index"
            class="deck-flip-card"
            :class="[
              `deck-pos-${getDeckPosition(index)}`,
              { 'is-active': activeCardIndex === index, flipped: flippedCards[index] }
            ]"
            @click="selectCard(index)"
          >
            <div class="flip-card-inner">
              <!-- FRONT: description text -->
              <div class="flip-card-face flip-card-front column">
                <h3 class="flip-card-title">{{ card.title }}</h3>
                <p class="flip-card-desc">{{ card.description }}</p>
                <button
                  type="button"
                  class="flip-circle-btn cleanIconButton"
                  aria-label="Show artwork"
                  @click.stop="toggleFlip(index)"
                  v-haptic
                >
                  <svg class="compactImg"><use href="@/assets/icons/iconset.svg#flip_compact" /></svg>
                </button>
              </div>
              <!-- BACK: Cara Vaggio image -->
              <div class="flip-card-face flip-card-back">
                <img :src="card.image" :alt="card.title" class="nft-image" />
                <button
                  type="button"
                  class="flip-circle-btn back-flip-btn cleanIconButton"
                  aria-label="Show artwork details"
                  @click.stop="toggleFlip(index)"
                  v-haptic
                >
                  <svg class="compactImg"><use href="@/assets/icons/iconset.svg#flip_compact" /></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import SingleSelectToggleRow from "@/components/GenericComponents/SingleSelectToggleRow.vue";
import LandingFeedPreview from "@/components/LandingComponents/LandingFeedPreview.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";

import cara1 from "@/assets/images/landing/cara1.jpg";
import cara2 from "@/assets/images/landing/cara2.jpg";
import cara3 from "@/assets/images/landing/cara3.jpg";

import mint02 from "@/assets/images/landing/mint-anything/mint-anything-02.jpg";
import mint03 from "@/assets/images/landing/mint-anything/mint-anything-03.jpg";
import mint04 from "@/assets/images/landing/mint-anything/mint-anything-04.jpg";
import mint06 from "@/assets/images/landing/mint-anything/mint-anything-06.jpg";
import mint11 from "@/assets/images/landing/mint-anything/mint-anything-11.jpg";
import mint12 from "@/assets/images/landing/mint-anything/mint-anything-12.jpg";
import mint13 from "@/assets/images/landing/mint-anything/mint-anything-13.jpg";
import mint15 from "@/assets/images/landing/mint-anything/mint-anything-15-framed.png";
import charlieBrown from "@/assets/images/landing/mint-anything/CharlieBrown.gif";

import * as THREE from "three";

export default {
  name: "LandingHero",
  components: { SingleSelectToggleRow, LandingFeedPreview, UnitButton },
  emits: ["login", "register"],
  setup() {
    const selectedFeed = ref("open");
    const feedOptions = [
      { id: "open", label: "Open Feed" },
      { id: "org", label: "Organisation Feed" },
    ];

    // Mint Anything slideshow images — all get polaroid frame via canvas
    const mintImageSrcs = [mint02, mint03, mint04, mint11, mint15, mint06, mint12, mint13, charlieBrown];
    const mintCanvasWrap = ref(null);

    // --- Three.js polaroid slideshow ---
    let threeCleanup = null;

    /** Draw image onto a canvas with a white polaroid border */
    function createPolaroidTexture(img) {
      const pad = 16;
      const bottomPad = 52;
      const cw = img.width + pad * 2;
      const ch = img.height + pad + bottomPad;
      const c = document.createElement("canvas");
      c.width = cw;
      c.height = ch;
      const ctx = c.getContext("2d");
      // White background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, cw, ch);
      // Subtle border shadow
      ctx.shadowColor = "rgba(0,0,0,0.08)";
      ctx.shadowBlur = 4;
      ctx.fillRect(0, 0, cw, ch);
      ctx.shadowBlur = 0;
      // Draw the photo
      ctx.drawImage(img, pad, pad, img.width, img.height);
      return c;
    }

    function initThreeSlideshow(container) {
      // Render larger than visible card so exit/enter animations don't crop
      const W = 500;
      const H = 420;
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(50, W / H, 0.1, 100);
      camera.position.set(0, 0, 5);

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setSize(W, H);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x000000, 0);
      container.appendChild(renderer.domElement);

      // Soft ambient + directional light for subtle shading
      scene.add(new THREE.AmbientLight(0xffffff, 0.85));
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
      dirLight.position.set(2, 3, 5);
      scene.add(dirLight);

      // Card mesh — will swap texture
      const cardGeo = new THREE.PlaneGeometry(2.2, 3.0);
      const cardMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        side: THREE.FrontSide,
        transparent: true,
        opacity: 1,
      });
      const cardMesh = new THREE.Mesh(cardGeo, cardMat);
      scene.add(cardMesh);

      // Shadow plane behind card
      const shadowGeo = new THREE.PlaneGeometry(2.4, 3.2);
      const shadowMat = new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.06,
        side: THREE.FrontSide,
      });
      const shadowMesh = new THREE.Mesh(shadowGeo, shadowMat);
      shadowMesh.position.z = -0.05;
      scene.add(shadowMesh);

      // Load all images → polaroid textures
      const textures = [];
      let loadedCount = 0;
      const loader = new THREE.TextureLoader();

      // Track GIF sources for animated texture updates
      const gifIndices = new Set();
      const gifImages = []; // { img, texIdx } for live repainting

      mintImageSrcs.forEach((src, srcIdx) => {
        const isGif = src.endsWith(".gif") || src.includes(".gif");
        const htmlImg = new Image();
        htmlImg.crossOrigin = "anonymous";
        htmlImg.onload = () => {
          // Scale image down for canvas (max 400px)
          const maxDim = 400;
          const scale = Math.min(maxDim / htmlImg.width, maxDim / htmlImg.height, 1);
          const sw = Math.round(htmlImg.width * scale);
          const sh = Math.round(htmlImg.height * scale);

          if (isGif) {
            // For GIFs: use the <img> element directly as texture source
            // so the browser keeps animating it; we'll set needsUpdate each frame
            const polaroidCanvas = createPolaroidTexture(htmlImg);
            const tex = new THREE.CanvasTexture(polaroidCanvas);
            tex.colorSpace = THREE.SRGBColorSpace;
            const texIdx = textures.length;
            textures.push(tex);
            gifIndices.add(texIdx);
            gifImages.push({ img: htmlImg, canvas: polaroidCanvas, texIdx });
          } else {
            // Static images: scale into a canvas
            const sc = document.createElement("canvas");
            sc.width = sw;
            sc.height = sh;
            sc.getContext("2d").drawImage(htmlImg, 0, 0, sw, sh);
            const polaroidCanvas = createPolaroidTexture(sc);
            const tex = new THREE.CanvasTexture(polaroidCanvas);
            tex.colorSpace = THREE.SRGBColorSpace;
            textures.push(tex);
          }
          loadedCount++;
          if (loadedCount === 1) {
            // Show first card immediately
            const tex = textures[0];
            cardMat.map = tex;
            cardMat.needsUpdate = true;
            const aspect = tex.image.width / tex.image.height;
            const cardH = 2.8;
            cardGeo.dispose();
            cardMesh.geometry = new THREE.PlaneGeometry(cardH * aspect, cardH);
          }
        };
        htmlImg.src = src;
      });

      // Animation state
      let currentIdx = 0;
      let phase = "idle"; // idle | leaving | entering
      let phaseTime = 0;
      const HOLD = 2.8;    // seconds to display
      const LEAVE = 0.5;   // seconds for exit
      const ENTER = 0.6;   // seconds for entrance
      let clock = new THREE.Clock();

      // Easing functions
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const easeInCubic = (t) => t * t * t;

      let animId = null;
      function animate() {
        animId = requestAnimationFrame(animate);
        const dt = clock.getDelta();
        if (textures.length === 0) {
          renderer.render(scene, camera);
          return;
        }

        // Repaint animated GIF textures every frame
        for (const g of gifImages) {
          if (currentIdx === g.texIdx) {
            const pad = 16;
            const ctx = g.canvas.getContext("2d");
            ctx.drawImage(g.img, pad, pad, g.canvas.width - pad * 2, g.canvas.height - pad - 52);
            textures[g.texIdx].needsUpdate = true;
          }
        }

        phaseTime += dt;

        if (phase === "idle") {
          // Card sits still with a gentle float
          cardMesh.rotation.y = Math.sin(phaseTime * 0.8) * 0.03;
          cardMesh.rotation.z = Math.sin(phaseTime * 0.5) * 0.01;
          cardMesh.position.y = Math.sin(phaseTime * 1.0) * 0.02;
          cardMat.opacity = 1;

          if (phaseTime >= HOLD) {
            phase = "leaving";
            phaseTime = 0;
          }
        } else if (phase === "leaving") {
          const t = Math.min(phaseTime / LEAVE, 1);
          const e = easeInCubic(t);
          cardMesh.rotation.y = -0.5 * e;
          cardMesh.position.x = -1.5 * e;
          cardMesh.position.z = -0.5 * e;
          cardMat.opacity = 1 - e;
          shadowMat.opacity = 0.06 * (1 - e);

          if (t >= 1) {
            // Swap texture
            currentIdx = (currentIdx + 1) % textures.length;
            cardMat.map = textures[currentIdx];
            cardMat.needsUpdate = true;
            // Adjust geometry aspect for new image
            const tex = textures[currentIdx];
            const aspect = tex.image.width / tex.image.height;
            const cardH = 2.8;
            cardMesh.geometry.dispose();
            cardMesh.geometry = new THREE.PlaneGeometry(cardH * aspect, cardH);
            phase = "entering";
            phaseTime = 0;
          }
        } else if (phase === "entering") {
          const t = Math.min(phaseTime / ENTER, 1);
          const e = easeOutCubic(t);
          cardMesh.rotation.y = 0.6 * (1 - e);
          cardMesh.position.x = 1.8 * (1 - e);
          cardMesh.position.z = -0.3 * (1 - e);
          cardMat.opacity = e;
          shadowMat.opacity = 0.06 * e;

          if (t >= 1) {
            cardMesh.rotation.y = 0;
            cardMesh.position.x = 0;
            cardMesh.position.z = 0;
            phase = "idle";
            phaseTime = 0;
          }
        }

        // Shadow follows card
        shadowMesh.position.x = cardMesh.position.x + 0.06;
        shadowMesh.position.y = cardMesh.position.y - 0.06;
        shadowMesh.rotation.y = cardMesh.rotation.y;
        shadowMesh.rotation.z = cardMesh.rotation.z;

        renderer.render(scene, camera);
      }
      animate();

      // Cleanup function
      return () => {
        if (animId) cancelAnimationFrame(animId);
        textures.forEach((t) => t.dispose());
        cardGeo.dispose();
        cardMat.dispose();
        shadowGeo.dispose();
        shadowMat.dispose();
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    }

    onMounted(() => {
      if (mintCanvasWrap.value) {
        threeCleanup = initThreeSlideshow(mintCanvasWrap.value);
      }
    });

    onUnmounted(() => {
      if (threeCleanup) threeCleanup();
    });

    // Cara Vaggio card data — English only
    const caraVaggioCards = [
      {
        title: "Cara Vaggio I",
        image: cara1,
        description:
          "Perfect focus with sharpness that highlights structural details. The visual definition allows one to fully appreciate the anatomy and texture.",
      },
      {
        title: "Cara Vaggio II",
        image: cara2,
        description:
          "Charismatic presence that transcends two-dimensional representation. The energy emanating from the image suggests personality and humanity beyond visual boundaries.",
      },
      {
        title: "Cara Vaggio III",
        image: cara3,
        description:
          "Chromatic harmony with radiant skin tones and natural glow. The tonal quality conveys health, vitality, and vibrant physical presence.",
      },
    ];

    // Deck + flip state
    const activeCardIndex = ref(0);
    const flippedCards = ref([false, false, false]);

    const selectCard = (index) => {
      if (activeCardIndex.value === index) {
        // Already in front — flip it
        flippedCards.value[index] = !flippedCards.value[index];
        return;
      }
      activeCardIndex.value = index;
    };

    // Returns 'left', 'center', or 'right' based on active card
    const getDeckPosition = (index) => {
      const active = activeCardIndex.value;
      if (index === active) return 'center';
      // Determine relative position
      const diff = index - active;
      if (diff < 0 || (diff === 2)) return 'left';
      return 'right';
    };

    const toggleFlip = (index) => {
      flippedCards.value[index] = !flippedCards.value[index];
    };

    return {
      selectedFeed,
      feedOptions,
      mintCanvasWrap,
      caraVaggioCards,
      activeCardIndex,
      flippedCards,
      selectCard,
      getDeckPosition,
      toggleFlip,
    };
  },
};
</script>

<style scoped>
/* ======================= */
/*  CONTAINER              */
/* ======================= */
.landing-hero-container {
  width: 100%;
  padding: var(--space-5);
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
}

/* ======================= */
/*  HERO ROW (shared)       */
/* ======================= */
.hero-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  width: 100%;
  padding: var(--space-8) 0;
}

@media (min-width: 768px) {
  .hero-row {
    flex-direction: row;
    align-items: center;
  }

  /* Zebra: right-aligned rows flip order */
  .row-right {
    flex-direction: row-reverse;
    text-align: right;
    margin: calc(var(--space-8) * 4) 0;
  }
  .row-right .row-text-side {
    align-items: flex-end;
  }

}

/* ======================= */
/*  TEXT SIDE (per row)     */
/* ======================= */
.row-text-side {
  flex: 1;
  gap: var(--space-5);
}

/* ======================= */
/*  VISUAL SIDE (per row)   */
/* ======================= */
.row-visual-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ======================= */
/*  CONSISTENT TITLE STYLE  */
/* ======================= */
.row-title {
  font-size: 3rem;
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--color-text-primary);
}

.highlight {
  color: var(--color-accent-emphasis);
}

.row-subtitle {
  font-size: var(--font-size-title);
  color: var(--color-text-tertiary);
  line-height: 1.5;
  margin: 0;
  max-width: 520px;
}

/* Right-aligned rows: push subtitle to the right */
@media (min-width: 768px) {
  .row-right .row-subtitle {
    margin-left: auto;
  }
}

/* ======================= */
/*  CTA ROW                 */
/* ======================= */
.cta-row {
  margin-top: var(--space-8);
}

/* ======================= */
/*  CARA VAGGIO DECK+FLIP   */
/* ======================= */
.deck-visual {
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.deck-container {
  position: relative;
  width: 280px;
  height: 360px;
}

.deck-flip-card {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  perspective: 1000px;
  cursor: pointer;
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1),
              opacity 0.5s ease,
              z-index 0s;
}

/* Deck positions: stacked left-to-right */
.deck-pos-left {
  transform: rotate(-10deg) translateX(-40px) scale(0.92);
  z-index: 1;
  opacity: 0.6;
}

.deck-pos-center {
  transform: rotate(0deg) translateX(0) scale(1);
  z-index: 3;
  opacity: 1;
}

.deck-pos-right {
  transform: rotate(10deg) translateX(40px) scale(0.92);
  z-index: 2;
  opacity: 0.6;
}

/* Active card glow */
.deck-flip-card.is-active {
  z-index: 3;
}

.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.deck-flip-card.flipped .flip-card-inner {
  transform: rotateY(180deg);
}

.flip-card-face {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 14px;
  border: 2px solid var(--color-surface-canvas);
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

/* FRONT: description text */
.flip-card-front {
  transform: rotateY(0deg);
  background-color: var(--color-surface-canvas);
  padding: var(--space-8);
  justify-content: flex-start;
  gap: var(--space-4);
}

.flip-card-title {
      font-size: var(--font-size-title);

  font-weight: 700;
  margin: 0;
  color: var(--color-text-primary);
}

.flip-card-desc {
      font-size: var(--font-size-title);

  color: var(--color-text-primary);
  line-height: 1.5;
  margin: 0;
  flex: 1;
}

/* BACK: image */
.flip-card-back {
  transform: rotateY(180deg);
  background-color: #2a2a2a;
}

.flip-card-back .nft-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Flip circle button */
.flip-circle-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: var(--control-height-compact);
  height: var(--control-height-compact);
  min-width: var(--control-height-compact);
  border-radius: 50%;
  padding: 0;
  border: 1px solid var(--color-border-subtle);
  background: transparent;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition-property: background-color, border-color, transform;
  transition-duration: var(--motion-standard);
  transition-timing-function: var(--motion-easing-standard);
}

.flip-circle-btn:hover {
  background: var(--color-surface-subtle);
  transform: translateY(var(--motion-pressed-offset));
}

.flip-circle-btn:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.back-flip-btn {
  background: rgba(0, 0, 0, 0.5);
  border-color: rgba(255, 255, 255, 0.3);
  color: var(--color-text-on-media);
}

.back-flip-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

/* Join CTA */
.cv-join-btn {
  margin-top: var(--space-7);
}

/* Description text below subtitle */
.row-description {
  font-size: var(--font-size-label);
  color: var(--color-text-muted);
  line-height: 1.5;
  margin: 0;
  max-width: 480px;
  font-style: italic;
}

/* ======================= */
/*  CARA VAGGIO OVERLAY     */
/* ======================= */

.gift-badge {
  background: var(--color-accent-emphasis);
  color: var(--color-text-on-accent);
  font-size: var(--font-size-micro);
  font-weight: 500;
  padding: var(--space-2) var(--space-3);
  border-radius: 100px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: var(--space-5);
  width: fit-content;
}



/* ======================= */
/*  ROW 3: ALGO-FREE        */
/* ======================= */
.algo-row {
  padding: var(--space-8) 0;
  flex-direction: column !important;
}

.algo-text-full {
  width: 100%;
  gap: var(--space-8);
}

.algo-manifesto {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: var(--color-text-tertiary);
  margin: 0;
}

/* Feed section — wraps toggle + masonry to avoid .algo-text-full gap */
.feed-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
}

/* Feed toggle area — matches ecosystem .feed-scope-toggle spacing */
.feed-toggle-area {
  margin: 0;
}

/* ======================= */
/*  CTA BUTTONS             */
/* ======================= */
.primary-btn {
  background: var(--color-text-primary);
  color: var(--color-text-on-accent);
  border: none;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: var(--font-size-body);
  font-weight: 600;
  cursor: pointer;
}

.secondary-btn {
  background: transparent;
  color: var(--color-text-primary);
  border: 2px solid var(--color-border-muted);
  padding: 12px 22px;
  border-radius: 12px;
  font-size: var(--font-size-body);
  font-weight: 600;
  cursor: pointer;
}

.secondary-btn:hover {
  border-color: var(--color-text-primary);
}

/* ======================= */
/*  MINT ANYTHING SLIDESHOW */
/* ======================= */
.mint-slideshow {
  display: flex;
  align-items: center;
  justify-content: center;
}

.polaroid-canvas-wrap {
  width: 340px;
  height: 380px;
  overflow: visible;
  position: relative;
}

.polaroid-canvas-wrap canvas {
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Don't constrain — let it overflow for animation room */
  width: auto !important;
  height: auto !important;
  pointer-events: none;
}

/* ======================= */
/*  MOBILE                  */
/* ======================= */
@media (max-width: 767px) {
  .row-title {
    font-size: 2.2rem;
  }

  .polaroid-canvas-wrap {
    width: 260px;
    height: 320px;
    margin: 0 auto;
  }

  .row-right {
    text-align: left;
  }

  .row-right .row-text-side {
    align-items: flex-start;
  }

  .deck-container {
    margin: 0 auto;
  }

  /* Cara Vaggio row: image first, then badge + CTA below */
  .row-right {
    display: flex;
    flex-direction: column;
  }
  .row-right .deck-visual {
    order: -1;
  }
}
</style>
